import React, { useContext, useEffect, useRef } from "react";
import "altcha";
import "altcha/i18n/pt-pt";
import "altcha/i18n/en";
import "altcha/i18n/es-es";
import "altcha/i18n/fr-fr";
import { useTranslation } from "react-i18next";
import { DigitalAssetContext } from "../../contexts/continente-credentials/DigitalAssetContext";

interface AltchaCaptchaProps {
  onVerified?: (response: string) => void;
  onError?: (error: string) => void;
  className?: string;
}

const AltchaCaptcha: React.FC<AltchaCaptchaProps> = ({
  onVerified,
  onError,
  className = "",
}) => {
  const digitalAssetContext = useContext(DigitalAssetContext);
  const { i18n } = useTranslation();
  const altchaRef = useRef<HTMLElement>(null);

  const getAltchaLanguage = (langCode: string): string => {
    switch (langCode) {
      case "pt":
      case "pt-PT":
        return "pt-pt";
      case "en":
      case "en-US":
      case "en-GB":
        return "en";
      default:
        return "pt-pt";
    }
  };

  useEffect(() => {
    // Only render if the provider is 'altcha'
    if (digitalAssetContext.digitalAsset.captchaProvider !== "altcha") {
      return;
    }

    const handleStateChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { state, payload } = customEvent.detail || {};

      if (state === "verified" && payload && onVerified) {
        onVerified(payload);
      } else if (state === "error" && onError) {
        onError(customEvent.detail?.error || "Altcha verification failed");
      }
    };

    // Add event listener to the altcha-widget element
    if (altchaRef.current) {
      altchaRef.current.addEventListener("statechange", handleStateChange);
    }

    return () => {
      // Cleanup when component unmounts
      if (altchaRef.current) {
        altchaRef.current.removeEventListener("statechange", handleStateChange);
      }
    };
  }, [digitalAssetContext.digitalAsset.captchaProvider, onVerified, onError]);

  const altchaLanguage = getAltchaLanguage(i18n.language);

  // Don't render if not using Altcha
  if (digitalAssetContext.digitalAsset.captchaProvider !== "altcha") {
    return null;
  }

  return (
    <div className={`altcha-container ${className}`}>
      <altcha-widget
        ref={altchaRef}
        challengeurl="/api/altcha/challenge"
        language={altchaLanguage}
        spamfilter={false}
        hidefooter={true}
        hidelogo={true}
      />
    </div>
  );
};

export default AltchaCaptcha;
